import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { alpha, useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { gridDetailPanelExpandedRowIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro'
import { format } from 'date-fns'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import T from 'T'
import { isUnread, type Notification } from 'types/modules/notification-center/notification'

import {
  CARD_GAP,
  CARD_PADDING_X,
  CARD_PADDING_Y,
  CARD_BORDER_RADIUS,
  CARD_TEXT_SPACING,
  SEVERITY_BADGE_SIZE,
  UNREAD_RAIL_WIDTH,
  UNREAD_TINT_OPACITY,
} from './notificationCenterConstants'
import { STATUS_LABELS, STATUS_PALETTE, customerLabelFor, severityOf } from './notificationCenterViews'
import NotificationEntityLink from './NotificationEntityLink'
import NotificationFacts from './NotificationFacts'
import NotificationSeverityBadge from './NotificationSeverityBadge'
import NotificationTypeChip from './NotificationTypeChip'
import OptimizationOutcomeChip from './OptimizationOutcomeChip'
import { useNotificationFacts } from './useNotificationFacts'
import { useNotificationReadState } from './useNotificationReadState'
import { reviewActionVariant, useOptimizationReview } from './useOptimizationReview'

// Squared off against the chips beside it: the one thing to press should not read as another pill.
const reviewButtonSx = { whiteSpace: 'nowrap', flexShrink: 0 }

const READ_TOGGLE_BUTTON_SX = { flexShrink: 0, minWidth: '8.5em' }

const UNREAD_DOT_SIZE = 9

const READ_TOGGLE_SIZE = 18

const READ_TOGGLE_SX = {
  width: READ_TOGGLE_SIZE,
  height: READ_TOGGLE_SIZE,
  ml: -0.25,
  flexShrink: 0,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  '&:hover': { bgcolor: 'action.selected' },
}

/** The card body toggles the panel, so the link must not bubble into it. */
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()

type ContextMenuPosition = { top: number; left: number }

type NotificationRowCardProps = {
  row: Notification
}

/**
 * One notification as a card, filling the grid's single column. Severity drives
 * the left rail, tint and unread dot; category drives the badge icon. The chevron
 * toggles the row's detail panel, so the card owns expansion instead of a
 * separate toggle column.
 */
const NotificationRowCard = ({ row }: NotificationRowCardProps) => {
  const theme = useTheme()
  const apiRef = useGridApiContext()
  const expandedIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector)
  const { markAsRead, markAsUnread, isUpdatingReadState } = useNotificationReadState()
  const navigate = useNavigate()
  const { reviewUrl, isApplied, isRejected, outcome } = useOptimizationReview(row)
  const facts = useNotificationFacts(row)

  const handleReviewClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      if (reviewUrl) navigate(reviewUrl)
    },
    [navigate, reviewUrl]
  )
  const [contextMenuPosition, setContextMenuPosition] = useState<ContextMenuPosition | null>(null)

  const expanded = expandedIds.includes(row.id)
  const unread = isUnread(row)
  const severityColor = theme.palette[severityOf(row)].main
  const statusPalette = STATUS_PALETTE[row.status]
  const statusColor = statusPalette === 'grey' ? theme.palette.grey[600] : theme.palette[statusPalette].main
  const customerLabel = customerLabelFor(row)

  const handleToggle = useCallback(() => apiRef.current.toggleDetailPanel(row.id), [apiRef, row.id])

  // Without stopping propagation the card's own onClick fires too and the two
  // toggles cancel out, leaving the row stuck closed.
  const handleChevronClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      handleToggle()
    },
    [handleToggle]
  )

  const handleToggleRead = useCallback(() => {
    if (isUpdatingReadState) return
    if (unread) markAsRead([row.id])
    else markAsUnread([row.id])
  }, [isUpdatingReadState, markAsRead, markAsUnread, row.id, unread])

  const handleReadToggleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      handleToggleRead()
    },
    [handleToggleRead]
  )

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setContextMenuPosition({ top: event.clientY, left: event.clientX })
  }, [])

  const handleCloseContextMenu = useCallback(() => setContextMenuPosition(null), [])

  const handleContextToggleRead = useCallback(() => {
    handleToggleRead()
    setContextMenuPosition(null)
  }, [handleToggleRead])

  const handleContextToggleExpand = useCallback(() => {
    handleToggle()
    setContextMenuPosition(null)
  }, [handleToggle])

  return (
    <Box
      sx={{
        width: '100%',
        mt: 0.75,
        mb: expanded ? 0 : 0.75,
        border: 1,
        borderColor: expanded ? 'primary.light' : 'divider',
        borderLeftWidth: UNREAD_RAIL_WIDTH,
        borderLeftColor: unread ? severityColor : 'divider',
        borderRadius: CARD_BORDER_RADIUS,
        // While open the panel below continues the card, so drop the seam between them.
        ...(expanded && { borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: 'none' }),
        bgcolor: unread ? alpha(severityColor, UNREAD_TINT_OPACITY) : 'transparent',
        boxShadow: !unread && !expanded ? 'none' : theme.shadows[1],
        cursor: 'pointer',
        // `borderColor` would repaint the rail too, dropping the severity colour on hover.
        '&:hover': { borderTopColor: 'primary.light', borderRightColor: 'primary.light', borderBottomColor: 'primary.light' },
        '&:hover .notification-read-toggle': { opacity: 1 },
      }}
      onClick={handleToggle}
      onContextMenu={handleContextMenu}
    >
      <Stack direction="row" spacing={CARD_GAP} sx={{ alignItems: 'flex-start', px: CARD_PADDING_X, py: CARD_PADDING_Y }}>
        <IconButton aria-label={expanded ? T.COLLAPSE : T.EXPAND} size="small" sx={{ ml: -1, flexShrink: 0 }} onClick={handleChevronClick}>
          <ChevronRightIcon
            sx={{
              color: 'text.lightBrown',
              transition: theme.transitions.create('transform'),
              transform: expanded ? 'rotate(90deg)' : 'none',
            }}
          />
        </IconButton>

        <NotificationSeverityBadge notification={row} size={SEVERITY_BADGE_SIZE} />

        <Stack spacing={CARD_TEXT_SPACING} sx={{ flex: 1, minWidth: 0 }}>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Tooltip arrow placement="top" title={unread ? T.MARK_AS_READ : T.MARK_AS_UNREAD}>
              <IconButton
                aria-label={unread ? T.MARK_AS_READ : T.MARK_AS_UNREAD}
                disabled={isUpdatingReadState}
                size="small"
                sx={READ_TOGGLE_SX}
                onClick={handleReadToggleClick}
              >
                <Box
                  className="notification-read-toggle"
                  sx={
                    unread
                      ? { width: UNREAD_DOT_SIZE, height: UNREAD_DOT_SIZE, borderRadius: '50%', bgcolor: severityColor }
                      : {
                          width: UNREAD_DOT_SIZE,
                          height: UNREAD_DOT_SIZE,
                          borderRadius: '50%',
                          border: 1,
                          borderColor: 'text.lightBrown',
                          opacity: 0,
                          transition: theme.transitions.create('opacity'),
                        }
                  }
                />
              </IconButton>
            </Tooltip>
            <NotificationTypeChip notification={row} />
            <Typography
              noWrap
              color={unread ? 'text.primary' : 'text.secondary'}
              component="p"
              sx={{ fontWeight: unread ? 700 : 500, minWidth: 0 }}
              variant="h6"
            >
              {row.title}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {/* The same tone the popover and toasts give a timestamp; `text.disabled` was too faint to read. */}
            <Typography noWrap color="text.lightBrown" sx={{ fontWeight: 400 }} variant="subtitle2">
              {format(new Date(row.occurredAt), 'MMM d, yyyy · h:mm a')}
            </Typography>
            {/* Fixed slot: read and unread differ in width, which otherwise shifts the timestamp per card. */}
            <Button size="small" sx={READ_TOGGLE_BUTTON_SX} variant="text" onClick={handleReadToggleClick}>
              {unread ? T.MARK_AS_READ : T.MARK_AS_UNREAD}
            </Button>
          </Stack>

          <Stack alignItems="center" direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            {customerLabel && (
              <NotificationEntityLink
                entityId={row.entityId}
                entityType={row.entityType}
                jobId={row.payload?.jobId}
                label={customerLabel}
                serviceDate={row.payload?.serviceDate}
                sx={{ fontWeight: 400 }}
                variant="subtitle2"
                onClick={stopPropagation}
              />
            )}
            {/* The route is already the link here, so only the remaining facts become chips. */}
            <NotificationFacts facts={customerLabel ? facts.filter(fact => fact.key !== 'route') : facts} />
            <OptimizationOutcomeChip outcome={outcome} />
            <Box sx={{ flex: 1 }} />
            {/* Two states, two chips: whether the optimizer ran, and what is left to do with the result. */}
            <Chip label={STATUS_LABELS[row.status]} size="small" sx={{ color: statusColor, borderColor: statusColor }} variant="outlined" />
            {reviewUrl && (
              <Button
                color={isApplied ? 'inherit' : 'primary'}
                size="small"
                startIcon={isApplied ? <CheckCircleOutlineIcon /> : <CompareArrowsIcon />}
                sx={reviewButtonSx}
                variant={reviewActionVariant({ isApplied, isRejected })}
                onClick={handleReviewClick}
              >
                {isApplied ? T.VIEW_APPLIED_OPTIMIZATION : T.REVIEW_OPTIMIZATION}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Menu
        anchorPosition={contextMenuPosition ?? undefined}
        anchorReference="anchorPosition"
        open={contextMenuPosition !== null}
        onClose={handleCloseContextMenu}
      >
        <MenuItem onClick={handleContextToggleRead}>{unread ? T.MARK_AS_READ : T.MARK_AS_UNREAD}</MenuItem>
        <MenuItem onClick={handleContextToggleExpand}>{expanded ? T.COLLAPSE : T.OPEN}</MenuItem>
      </Menu>
    </Box>
  )
}

export default NotificationRowCard
